import { useState, useEffect } from "react";
import { Preferences } from "@capacitor/preferences";

interface TermsAgreementState {
  hasAgreed: boolean;
  showModal: boolean;
  isLoading: boolean;
}

export const useTermsAgreement = () => {
  const [state, setState] = useState<TermsAgreementState>({
    hasAgreed: false,
    showModal: false,
    isLoading: true,
  });

  // Check if user has agreed to terms on component mount
  useEffect(() => {
    const checkTermsAgreement = async () => {
      try {
        const { value: termsAgreed } = await Preferences.get({ key: "termsAgreed" });
        const hasAgreed = termsAgreed === "true";
        
        setState({
          hasAgreed,
          showModal: !hasAgreed,
          isLoading: false,
        });
      } catch (error) {
        console.error("Error checking terms agreement:", error);
        setState({
          hasAgreed: false,
          showModal: true,
          isLoading: false,
        });
      }
    };

    checkTermsAgreement();
  }, []);

  const agreeToTerms = async () => {
    try {
      await Preferences.set({ key: "termsAgreed", value: "true" });
      await Preferences.set({ key: "termsAgreedDate", value: new Date().toISOString() });
      
      setState(prev => ({
        ...prev,
        hasAgreed: true,
        showModal: false,
      }));
    } catch (error) {
      console.error("Error saving terms agreement:", error);
    }
  };

  const showTermsModal = () => {
    setState(prev => ({
      ...prev,
      showModal: true,
    }));
  };

  const hideTermsModal = () => {
    setState(prev => ({
      ...prev,
      showModal: false,
    }));
  };

  const resetTermsAgreement = async () => {
    try {
      await Preferences.remove({ key: "termsAgreed" });
      await Preferences.remove({ key: "termsAgreedDate" });
      
      setState({
        hasAgreed: false,
        showModal: true,
        isLoading: false,
      });
    } catch (error) {
      console.error("Error resetting terms agreement:", error);
    }
  };

  const getAgreementDate = async (): Promise<string | null> => {
    try {
      const { value } = await Preferences.get({ key: "termsAgreedDate" });
      return value;
    } catch (error) {
      console.error("Error getting agreement date:", error);
      return null;
    }
  };

  return {
    hasAgreed: state.hasAgreed,
    showModal: state.showModal,
    isLoading: state.isLoading,
    agreeToTerms,
    showTermsModal,
    hideTermsModal,
    resetTermsAgreement,
    getAgreementDate,
  };
};
